// packages/api-client/src/index.ts

// --- Internal Client Dependencies ---
import {
  configureApiClient as configureInternalAxios,
  getApiClient as getInternalAxios,
  getRawApiBaseUrl as getRawApiBaseUrlInternal,
  setAuthHeaderProvider,
  setAuthRefresh,
  type ApiClientConfig,
} from './client';

// --- Import All Service Implementations ---
import { agentService } from './services/agentService';
import { apiKeyService } from './services/apiKeyService';
import { billingService } from './services/billingService';
import { cmsService } from './services/public/cmsService';
import { conversationService } from './services/conversationService';
import { commentsService } from './services/commentsService'; // New
import { threadsService } from './services/threadsService';   // New
import { knowledgeService } from './services/knowledgeService';
import { modelService } from './services/modelService';
import { promptService } from './services/promptService';
import { sprintService } from './services/public/sprintService';
import { toolService } from './services/toolService';
import { userService } from './services/userService';
import { visualizationService } from './services/visualizationService';
import { widgetService, normalizeUuid } from './services/widgetService';
import { widgetInstallationsService } from './services/widgetInstallationsService';
import { agentTestChatService } from './services/agentTestChatService';
import { visitorConversationsService } from './services/visitorConversationsService';
import { publicAgentService } from './services/public/for later/publicAgentService';
import { goalService } from './services/public/goalService';
import { projectService } from './services/public/projectService';
import { taskService } from './services/public/taskService';
import { portfolioService } from './services/public/portfolioService';
import { agentVersionsService } from './services/public/agentsVersionsService';
import { widgetsService } from './services/widgetsService';
import { featuresCatalogService } from './services/public/featuresCatalogService';
import { leadsService } from './services/public/leadsService';
import { publicAgentProfilesService } from './services/public/publicAgentProfilesService';
import * as publicServices from './services/public';
import { adminCmsService } from './services/admin/adminCmsService';
import { adminPaymentsService } from './services/admin/adminPaymentsService';
import { adminSprintService } from './services/admin/adminSprintService';
import { adminGoalService } from './services/admin/adminGoalService';
import { adminUserService } from './services/admin/adminUserService';
import { adminTransactionsService } from './services/admin/adminTransactionsService';
import { adminFinanceService } from './services/admin/adminFinanceService';
import { adminProjectService } from './services/admin/adminProjectService';
import { adminTaskService } from './services/admin/adminTaskService';
import { adminAgentService } from './services/admin/adminAgentService';
import { adminArticlesService } from './services/admin/adminArticlesService';
import { adminImageStylesService } from './services/admin/imageStyles';
import { adminInvestmentsService } from './services/admin/adminInvestmentsService';
import { shareService } from './services/shareService';
import { summaryAgentService } from './services/summaryAgentService';
import { secretsService } from './services/secretsService';
import { toolTemplateService } from './services/toolTemplateService';
import { myWidgetConversationsService } from './services/myWidgetConversationsService';
import { operatorService } from './services/operatorService';
import { mcpService } from './services/mcpService';
import { filesService } from './services/kb/files';
import { fileGroupsService } from './services/kb/fileGroups';
import { filesLibraryService } from './services/kb/filesLibrary';

import { contextAnalysisService } from './services/contextAnalysisService';
import { conversationUsageService } from './services/conversationUsageService';
import { suggestionsService } from './services/suggestionsService';

import { contentEngineService } from './services/contentEngineService'
import { automationsContentService } from './services/automationsContentService'
import { fsService } from './services/kb/fsService'

import * as adminServices from './services/admin';

import { quickstartService } from './services/quickstartService';

// ===================================================================
// --- NEW PATTERN: Structured Client (Recommended for new code) ---
// ===================================================================

export type ApiClient = {
  agent: typeof agentService;
  apiKey: typeof apiKeyService;
  billing: typeof billingService;
  cms: typeof cmsService;
  conversation: typeof conversationService;
  comments: typeof commentsService; // New
  threads: typeof threadsService;   // New
  featuresCatalog: typeof featuresCatalogService;
  goal: typeof goalService;
  knowledge: typeof knowledgeService;
  model: typeof modelService;
  portfolio: typeof portfolioService;
  project: typeof projectService;
  prompt: typeof promptService;
  public: typeof publicServices;
  publicAgent: typeof publicAgentService;
  publicAgentProfiles: typeof publicAgentProfilesService;
  sprint: typeof sprintService;
  task: typeof taskService;
  tool: typeof toolService;
  user: typeof userService;
  versions: typeof agentVersionsService;
  visualization: typeof visualizationService;
  visitorConversations: typeof visitorConversationsService;
  widget: typeof widgetService;
  widgetInstallations: typeof widgetInstallationsService;
  widgets: typeof widgetsService;
  agentTestChat: typeof agentTestChatService;
  leads: typeof leadsService;
  quickstart: typeof quickstartService;
  share: typeof shareService;
  summary: typeof summaryAgentService;
  secrets: typeof secretsService;
  template: typeof toolTemplateService;
  operator: typeof operatorService;
  mywidget: typeof myWidgetConversationsService;
  mcp: typeof mcpService;
  contentEngine: typeof contentEngineService;
  automationsContent: typeof automationsContentService;
  fs: typeof fsService;
  files: typeof filesService;
  fileGroups: typeof fileGroupsService;
  filesLibrary: typeof filesLibraryService;
  contextAnalysis: typeof contextAnalysisService;
  conversationUsage: typeof conversationUsageService;
  suggestions: typeof suggestionsService;

  admin: {
    agent: typeof adminAgentService;
    articles: typeof adminArticlesService;
    cms: typeof adminCmsService;
    finance: typeof adminFinanceService;
    goal: typeof adminGoalService;
    imageStyles: typeof adminImageStylesService;
    investments: typeof adminInvestmentsService;
    payments: typeof adminPaymentsService;
    project: typeof adminProjectService;
    sprint: typeof adminSprintService;
    task: typeof adminTaskService;
    transactions: typeof adminTransactionsService;
    user: typeof adminUserService;
    agentVersions: typeof adminServices.adminAgentVersionsService;
  };
  _axios: () => ReturnType<typeof getInternalAxios>;
  _rawApiBaseUrl: () => string;
};

export function createApiClient(config: ApiClientConfig): ApiClient {
  configureInternalAxios(config);
  return {
    agent: agentService,
    apiKey: apiKeyService,
    billing: billingService,
    cms: cmsService,
    conversation: conversationService,
    comments: commentsService, // New
    threads: threadsService,   // New
    featuresCatalog: featuresCatalogService,
    goal: goalService,
    knowledge: knowledgeService,
    model: modelService,
    portfolio: portfolioService,
    project: projectService,
    prompt: promptService,
    public: publicServices,
    publicAgent: publicAgentService,
    publicAgentProfiles: publicAgentProfilesService,
    sprint: sprintService,
    task: taskService,
    tool: toolService,
    user: userService,
    versions: agentVersionsService,
    visualization: visualizationService,
    visitorConversations: visitorConversationsService,
    widget: widgetService,
    widgetInstallations: widgetInstallationsService,
    widgets: widgetsService,
    agentTestChat: agentTestChatService,
    leads: leadsService,
    quickstart: quickstartService,
    share: shareService,
    summary: summaryAgentService,
    secrets: secretsService,
    template: toolTemplateService,
    operator: operatorService,
    mywidget: myWidgetConversationsService,
    mcp: mcpService,
    contentEngine: contentEngineService,
    automationsContent: automationsContentService,
    fs: fsService,
    files: filesService,
    fileGroups: fileGroupsService,
    filesLibrary: filesLibraryService,
    contextAnalysis: contextAnalysisService,
    conversationUsage: conversationUsageService,
    suggestions: suggestionsService,

    admin: {
      agent: adminAgentService,
      articles: adminArticlesService,
      cms: adminCmsService,
      finance: adminFinanceService,
      goal: adminGoalService,
      imageStyles: adminImageStylesService,
      investments: adminInvestmentsService,
      payments: adminPaymentsService,
      project: adminProjectService,
      sprint: adminSprintService,
      task: adminTaskService,
      transactions: adminTransactionsService,
      user: adminUserService,
      agentVersions: adminServices.adminAgentVersionsService,
    },
    _axios: getInternalAxios,
    _rawApiBaseUrl: getRawApiBaseUrlInternal,
  };
}

// Global store for resilient singletons across duplicates
const g = globalThis as any;
g.dopomog_api_client_structured = g.dopomog_api_client_structured || { client: null as ApiClient | null };

export function configureApiClient(configOrClient: ApiClientConfig | { client: ApiClient }): void {
  if ('client' in configOrClient) {
    g.dopomog_api_client_structured.client = configOrClient.client;
  } else {
    g.dopomog_api_client_structured.client = createApiClient(configOrClient);
  }
}

export function getApiClient(): ApiClient {
  const inst = (globalThis as any).dopomog_api_client_structured?.client;
  if (!inst) {
    throw new Error('API Client has not been configured. Call configureApiClient() first.');
  }
  return inst;
}


// ===================================================================
// --- 👇 BACKWARD COMPATIBILITY EXPORTS 👇 ---
// ===================================================================

// Public-Facing Services
export { agentService };
export { apiKeyService };
export { billingService };
export { cmsService };
export { conversationService };
export { commentsService }; // New
export { threadsService };  // New
export { knowledgeService };
export { modelService };
export { promptService };
export { sprintService };
export { toolService };
export { userService };
export { visualizationService };
export { widgetService };
export { widgetInstallationsService };
export { agentTestChatService };
export { visitorConversationsService };
export { publicAgentService };
export { goalService };
export { projectService };
export { taskService };
export { portfolioService };
export { agentVersionsService };
export { widgetsService };
export { normalizeUuid };
export { featuresCatalogService };
export { leadsService };
export { publicAgentProfilesService };
export { quickstartService };
export { shareService };
export { summaryAgentService };
export { secretsService };
export { toolTemplateService };
export { operatorService };
export { myWidgetConversationsService };
export { mcpService };
export { filesService };
export { fileGroupsService };
export { filesLibraryService };
export { contextAnalysisService }
export { conversationUsageService }
export { suggestionsService };

export * from './services/public';

export { contentEngineService }
export { automationsContentService }
export { fsService }

// Admin-Specific Services
export { adminCmsService };
export { adminPaymentsService };
export { adminSprintService };
export { adminGoalService };
export { adminUserService };
export { adminTransactionsService };
export { adminFinanceService };
export { adminProjectService };
export { adminTaskService };
export { adminAgentService };
export { adminArticlesService };
export { adminImageStylesService };
export { adminInvestmentsService };

export * from './services/admin';


// ===================================================================
// --- Utility and Type Re-exports ---
// ===================================================================

export { handleApiError } from './utils/handleApiError';
import type { ApiErrorResponse, ApiSuccessResponse, PaginatedResponse, ServiceResponse } from '@dopomogai/types';

export function isApiError(response: unknown): response is ApiErrorResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'isError' in response &&
    (response as ApiErrorResponse).isError === true
  );
}

export { setAuthRefresh, setAuthHeaderProvider };

export type { ApiClientConfig };
export type { ApiErrorResponse, ApiSuccessResponse, PaginatedResponse, ServiceResponse };

// Specific Service Types
export type { CommentItem } from './services/commentsService';
export type { ThreadCreateResponse, ThreadSummary } from './services/threadsService';

export type { AdminVersionAvatarGenRequest } from './services/admin/adminAgentVersionsService';
export type { InvestmentListFilters } from './services/admin/adminInvestmentsService';
export type { AdminCreateInvoicePayload, AdminCreateInvoiceResponse, BasketItem, InvoiceLog, MonobankInvoiceStatus, } from './services/admin/adminPaymentsService';
export type { UserListFilters } from './services/admin/adminUserService';
export type { MonobankAccount, MonobankClientInfo, BalanceSnapshot, } from './services/admin/adminFinanceService';
export type { UUID, HttpUrl, CreditTransactionItem, CreditTransactionType, LLMUsageSummaryResponse, CreditPackageDisplay, AvailableCreditPackagesResponse, CreateMonobankCheckoutPayload, CreateMonobankCheckoutResponse, UserDB, InvestmentRecord, } from '@dopomogai/types';
export type { ConversationUsageLogsResponse, ConversationUsageLogItem } from './services/conversationUsageService';
export type { SuggestionsResponse } from './services/suggestionsService';
export { widgetFilesService } from './services/widgetFilesService';
